import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { CircularProgress, Stack, Typography } from "@mui/material";
import { Dayjs } from "dayjs";
import { useActionHook, useFormatDateHook } from "hooks";
import { useDealRoleGuard } from "hooks/deal-hooks/use-deal-role-guard";
import { useDataRoomsActions, useDateFormatSelect } from "store";
import { isYesterday } from "utils";

import { RoleName } from "../../constants";

interface Props {
  dealId?: number;
}

export const Indicator: React.FC<Props> = ({ dealId }) => {
  const { getDealDataroomsActions } = useDataRoomsActions();
  const formatDateHook = useFormatDateHook();
  const dateFormat = useDateFormatSelect();
  const formatDate = (date?: string | number | Date | Dayjs | null | undefined) => {
    return formatDateHook({ date, format: dateFormat });
  };
  const history = useHistory();
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const handleItemClick = (item: { id: number }) => {
    if (dealId) {
      history.push(`/main/data_rooms/${item.id}`);
    }
  };

  // Create a permission-guarded version of the click handler
  const handleItemClickWithPermission = useDealRoleGuard(handleItemClick, [RoleName.Admin]);

  const { action, data = [], loading } = useActionHook(getDealDataroomsActions);

  useEffect(() => {
    dealId && action(dealId);
  }, []);

  if (loading) {
    return (
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          gap: "12px",
          p: "16px",
          maxHeight: "80px"
        }}
      >
        <CircularProgress size={20} />
        <Typography
          sx={{
            color: "#64748B",
            fontSize: "14px",
            fontWeight: 500
          }}
        >
          Loading connection...
        </Typography>
      </Stack>
    );
  }

  if (data?.length) {
    return (
      <>
        {/* Placeholder behind - takes space of first item */}
        <Stack
          sx={{
            backgroundColor: "transparent",
            borderRadius: "12px",
            border: "1px solid transparent",
            minHeight: "80px"
          }}
        />

        {/* Floating list */}
        <Stack
          sx={{
            backgroundColor: "#FFFFFF",
            borderRadius: "12px",
            border: "1px solid #F1F5F9",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
            position: "absolute",
            top: "20px",
            right: "20px",
            overflow: "hidden",
            transition: "all 0.6s ease-in-out",
            opacity: 1
          }}
        >
          {data.map((item, index) => {
            // Only show first item when collapsed, or all items when expanded
            if (!isExpanded && index) return null;

            return (
              <Stack
                key={item.id}
                sx={{
                  transition: "opacity 0.6s ease-in-out",
                  opacity: isExpanded || index === 0 ? 1 : 0
                }}
              >
                <Stack
                  direction="row"
                  onClick={() => handleItemClickWithPermission?.(item)}
                  sx={{
                    gap: "12px",
                    alignItems: "center",
                    minHeight: "80px",
                    p: "16px",
                    cursor: handleItemClickWithPermission ? "pointer" : "default",
                    "&:hover": {
                      backgroundColor: handleItemClickWithPermission ? "#F8FAFC" : "transparent"
                    }
                  }}
                >
                  <img
                    src={item.logo}
                    alt="Data room logo"
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "8px",
                      flexShrink: 0
                    }}
                  />
                  <Stack sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                    <Typography
                      sx={{
                        color: "#0F172A",
                        fontSize: "14px",
                        fontWeight: 700,
                        lineHeight: "20px",
                        mb: "2px",
                        letterSpacing: "-0.025em",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                      }}
                    >
                      {item.type}
                    </Typography>
                    <Typography
                      sx={{
                        color: "#475569",
                        fontSize: "12px",
                        fontWeight: 500,
                        lineHeight: "16px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                      }}
                    >
                      {item.username}
                    </Typography>
                    {item.last_connected && (
                      <Typography
                        sx={{
                          color: "#64748B",
                          fontSize: "11px",
                          fontWeight: 400,
                          lineHeight: "14px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          mt: "2px"
                        }}
                      >
                        <span
                          style={{
                            width: "6px",
                            height: "6px",
                            backgroundColor: isYesterday(item.last_connected) ? "#10B981" : "#D97706",
                            borderRadius: "50%",
                            display: "inline-block",
                            flexShrink: 0
                          }}
                        />
                        Last: {formatDate(item.last_connected)}
                      </Typography>
                    )}
                  </Stack>

                  {/* Arrow for first item when multiple datarooms */}
                  {index === 0 && data.length > 1 && (
                    <Stack
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpanded();
                      }}
                      sx={{
                        cursor: "pointer",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "24px",
                        height: "24px"
                      }}
                    >
                      {isExpanded ? (
                        <KeyboardArrowUpIcon
                          sx={{
                            color: "#64748B",
                            fontSize: "20px",
                            transition: "transform 0.2s ease-in-out"
                          }}
                        />
                      ) : (
                        <KeyboardArrowDownIcon
                          sx={{
                            color: "#64748B",
                            fontSize: "20px",
                            transition: "transform 0.2s ease-in-out"
                          }}
                        />
                      )}
                    </Stack>
                  )}
                </Stack>
                {/* Separator between items (except last) */}
                {index < data.length - 1 && isExpanded && (
                  <div
                    style={{
                      height: "1px",
                      backgroundColor: "#E2E8F0",
                      width: "100%"
                    }}
                  />
                )}
              </Stack>
            );
          })}
        </Stack>
      </>
    );
  }

  return null;
};
